import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {ListResponse} from '../interface/response/list-response';
import {HttpClient} from '@angular/common/http';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {first, take} from 'rxjs/operators';
import {Uuid} from '../interface/response/uuid';
import {Invoice} from '../interface/invoice';

@Injectable({
  providedIn: 'root'
})
export class InvoiceService {
  public readonly invoiceListSubject: ReplaySubject<CountedAndPaginated<Invoice>> = new ReplaySubject<CountedAndPaginated<Invoice>>(1);
  public readonly invoiceNotCountedListSubject: ReplaySubject<ListResponse<Invoice>> = new ReplaySubject<ListResponse<Invoice>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  public sendForm(formData: object, invoiceId?: number): Observable<any> {
    if (invoiceId) {
      return this.httpClient.put(`/rest/crm/invoice/${invoiceId}`, formData)
        .pipe(
          take(1)
        );
    } else {
      return this.httpClient.post('/rest/crm/invoice', formData)
        .pipe(
          take(1)
        );
    }
  }

  public loadList(
    page: number,
    pageSize: number,
    filters: object,
    sort?: {
      sortBy?: string,
      sortOrder?: string,
    },
    subject?: Subject<CountedAndPaginated<Invoice>>): void {
    this.httpClient.get(`/rest/crm/invoice?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Invoice>) => {
        if (subject) {
          subject.next(result);
        } else {
          this.invoiceListSubject.next(result);
        }
      });
  }

  public loadNotCountedList(page: number, pageSize: number, filters: object, observable?: ReplaySubject<ListResponse<Invoice>>): void {
    this.httpClient
      .get(`/rest/crm/invoice?disableCount=1&page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<Invoice>) => {
        if (observable) {
          observable.next(result);
        } else {
          this.invoiceNotCountedListSubject.next(result);
        }
      });
  }

  public downloadReport(filters: object,  sort?: { sortBy?: string, sortOrder?: string}): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/invoice/method/report?&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public get(invoiceId: number): Observable<Invoice> {
    return this.httpClient.get(`/rest/crm/invoice/${invoiceId}`)
      .pipe(first()) as Observable<Invoice>;
  }

  public delete(invoiceId: number): Observable<any> {
    return this.httpClient.delete(`/rest/crm/invoice/${invoiceId}`)
      .pipe(first());
  }

  public downloadPdf(invoiceId: number): void {
    this.document.location.href = `/rest/crm/invoice/${invoiceId}/downloadFile`;
  }

  public changeStatus(uuid: string, actionCode: string, formData: any): Observable<any> {
    return this.httpClient.patch(`/rest/crm/invoice/${uuid}/${actionCode}`, formData)
      .pipe(first());
  }

  public addDocument(uuid: string, formData: any): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/invoice/${uuid}/document`, formData)
      .pipe(first()) as Observable<Uuid>;
  }

  public downloadDocument(invoiceId: string, documentId: string): void {
    this.document.location.href = `/rest/crm/invoice/${invoiceId}/document/${documentId}/downloadFile`;
  }

  decodeBillAdd(formData: any): Observable<any> {
    return this.httpClient.post(`/rest/crm/bill/method/decode`, formData)
      .pipe(first());
  }
  public checkInvoice(contractId: string, number: string, gross: number ): Observable<Invoice[]> {
    return this.httpClient.get(`/rest/crm/check-invoice/${contractId}/${number}/${gross}`)
      .pipe(first()) as Observable<Invoice[]>;
  }

  public deleteDocument(uuid: string): Observable<any> {
    return this.httpClient.delete(`/rest/crm/invoice/${uuid}/document`)
      .pipe(first());
  }
}
